import { FC } from 'react';
import { KatModal, KatButton } from '@amzn/katal-react';
import { AssetState } from 'src/types';
import { useCropLogic } from './useCropLogic';
import styles from './CropModal.module.css';

export type CropOrientation = 'vertical' | 'horizontal';

interface CropModalProps {
  isVisible: boolean;
  onClose?: () => void;
  imageState: AssetState;
  updateAssetState: (state: Partial<AssetState>) => void;
  imageKey?: string;
  disabled?: boolean;
  orientation: CropOrientation;
  'data-testid': string;
}

export const CropModal: FC<CropModalProps> = ({
  isVisible,
  onClose,
  imageState,
  updateAssetState,
  imageKey,
  disabled = false,
  orientation,
  'data-testid': dataTestId,
}) => {
  const {
    cropBoxPosition,
    cropBoxSize,
    imageRef,
    handleImageLoad,
    handleMouseDown,
    handleTouchStart,
    handleSave,
  } = useCropLogic({ isVisible, imageState, orientation });

  const isNewImage =
    orientation === 'vertical'
      ? imageState.topOffset === undefined
      : imageState.leftOffset === undefined;

  const handleCancel = () => {
    if (!isNewImage) {
      onClose?.();
    }
  };

  const onSave = () => {
    handleSave(updateAssetState, onClose);
  };

  if (!isVisible) return null;

  const isHorizontal = orientation === 'horizontal';

  const cropBoxStyle = isHorizontal
    ? { width: cropBoxSize, left: cropBoxPosition }
    : { height: cropBoxSize, top: cropBoxPosition };

  return (
    <KatModal
      visible={isVisible}
      onClose={isNewImage ? undefined : onClose}
      className={styles.modal}
      persistent={isNewImage}
      data-testid="crop-modal"
    >
      <span slot="title">Drag the highlighted area to select the portion you want to keep</span>
      <div className={styles.modalContent}>
        <div className={styles.imageContainer} data-testid={dataTestId}>
          <img
            ref={imageRef}
            src={imageKey}
            alt="Crop preview"
            className={styles.image}
            onLoad={handleImageLoad}
            draggable={false}
          />
          <div
            className={isHorizontal ? styles.cropBoxHorizontal : styles.cropBox}
            style={cropBoxStyle}
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
          />
        </div>
      </div>
      <div slot="footer" className={styles.footer}>
        {!isNewImage && (
          <KatButton
            variant="secondary"
            onClick={handleCancel}
            disabled={disabled}
            data-testid={`${dataTestId}-cancel-button`}
          >
            Cancel
          </KatButton>
        )}
        <KatButton
          variant="primary"
          onClick={onSave}
          disabled={disabled}
          data-testid={`${dataTestId}-save-button`}
          className={styles.saveButton}
        >
          Save
        </KatButton>
      </div>
    </KatModal>
  );
};
